import { colors } from '@kmns-internal/furikake'
import {
  ChevronRight,
  DeleteOutlined,
  ExpandMore,
  FolderShared,
  MoreHoriz,
  Person,
} from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import type { User, UserGroup } from '@ulysses-inc/harami_api_client'
import React, { type MouseEvent, useState } from 'react'
import LoadingOverlay from 'src/components/loading/LoadingOverlay'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import {
  useMutationDeleteUserGroup,
  useMutationValidateDeleteUserGroup,
} from './userGroups/api'

interface Props {
  userGroups: UserGroup[]
  openEditUserGroup: (userGroupId: number) => void
}

const USER_INDENT_PX = 52
const ACTION_COLUMN_MIN_WIDTH = '96px'

const UserGroupList: React.FC<Props> = ({ userGroups, openEditUserGroup }) => {
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar()

  const validateDeleteMutation = useMutationValidateDeleteUserGroup()
  const deleteMutation = useMutationDeleteUserGroup()

  const [collapsedGroup, setCollapsedGroup] = useState<number[]>([])

  // 3点リーダーメニュー
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedRow, setSelectedRow] = useState<UserGroup | undefined>(
    undefined,
  )
  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    userGroup: UserGroup,
  ) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    setSelectedRow(userGroup)
  }
  const handleMenuClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(null)
  }

  // 削除確認ダイアログ
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  // 削除エラーダイアログ
  const [openErrorDialog, setOpenErrorDialog] = useState(false)
  const [errorMessages, setErrorMessages] = useState<string[]>([])

  // 削除 MenuItem
  const handleMenuDelete = (
    event: MouseEvent<HTMLElement>,
    userGroup: UserGroup | undefined,
  ) => {
    event.stopPropagation()
    if (userGroup !== undefined) {
      handleDeleteClick(userGroup)
    }
    handleMenuClose(event)
  }

  // アコーディオン
  const closeGroup = (
    event: React.MouseEvent<HTMLElement>,
    userGroupId: number,
  ) => {
    setCollapsedGroup([...collapsedGroup, userGroupId])
    event.preventDefault()
    event.stopPropagation()
  }
  const openGroup = (
    event: React.MouseEvent<HTMLElement>,
    userGroupId: number,
  ) => {
    setCollapsedGroup([...collapsedGroup.filter(id => id !== userGroupId)])
    event.preventDefault()
    event.stopPropagation()
  }

  const handleDeleteClick = async (userGroup: UserGroup) => {
    const userGroupId = userGroup.id
    if (!userGroupId) {
      showErrorSnackbar('ユーザーグループIDが取得できません')
      return
    }

    try {
      const result = await validateDeleteMutation.mutateAsync({
        userGroupId,
      })

      if (!result.canDelete) {
        const splitErrMsg = (result.errorMessage ?? '').split('\n')
        setErrorMessages(splitErrMsg)
        setOpenErrorDialog(true)
        return
      }

      setOpenConfirmDialog(true)
    } catch {
      showErrorSnackbar('ユーザーグループの削除に失敗しました')
    }
  }

  const handleConfirmDelete = async () => {
    const userGroupId = selectedRow?.id
    if (!userGroupId) {
      showErrorSnackbar('削除対象のユーザーグループが選択されていません')
      setOpenConfirmDialog(false)
      return
    }

    try {
      await deleteMutation.mutateAsync({
        userGroupId,
      })
      showSuccessSnackbar('ユーザーグループを削除しました')
      setOpenConfirmDialog(false)
    } catch {
      showErrorSnackbar('ユーザーグループの削除に失敗しました')
    }
  }

  const isValidating =
    validateDeleteMutation.isPending || deleteMutation.isPending

  return (
    <>
      <LoadingOverlay
        spinning={isValidating}
        size="large"
        render={
          <Table sx={{ width: '100%' }} stickyHeader>
            <TableHead sx={{ backgroundColor: 'white' }}>
              <TableRow>
                <TableCell
                  size="small"
                  sx={{
                    whiteSpace: 'normal',
                    backgroundColor: 'white',
                  }}
                >
                  ユーザーグループ名
                </TableCell>
                <TableCell
                  size="small"
                  sx={{
                    whiteSpace: 'normal',
                    backgroundColor: 'white',
                  }}
                >
                  {/* アクション列 */}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ backgroundColor: 'white' }}>
              {userGroups.map((userGroup: UserGroup) => {
                const name = userGroup.name
                const isOpen = userGroup.id
                  ? !collapsedGroup.includes(userGroup.id)
                  : false
                return (
                  <React.Fragment key={userGroup.id}>
                    <TableRow
                      onClick={() => openEditUserGroup(userGroup.id ?? 0)}
                      hover
                      sx={{
                        cursor: 'pointer',
                      }}
                    >
                      <TableCell
                        size="small"
                        sx={{
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          pl: 0,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <IconButton
                            onClick={e => {
                              if (userGroup.id) {
                                if (isOpen) {
                                  closeGroup(e, userGroup.id)
                                } else {
                                  openGroup(e, userGroup.id)
                                }
                              }
                            }}
                          >
                            {isOpen ? <ExpandMore /> : <ChevronRight />}
                          </IconButton>
                          <FolderShared
                            fontSize="small"
                            sx={{ color: colors.text.secondary }}
                          />
                          {name}
                        </Typography>
                      </TableCell>
                      <TableCell
                        size="small"
                        sx={{ minWidth: ACTION_COLUMN_MIN_WIDTH }}
                        align="right"
                      >
                        <IconButton
                          onClick={e => {
                            handleMenuClick(e, userGroup)
                          }}
                        >
                          <MoreHoriz />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {userGroup.users &&
                      userGroup.users.length > 0 &&
                      userGroup.id &&
                      !collapsedGroup.includes(userGroup.id) &&
                      userGroup.users.map((user: User) => (
                        <TableRow key={user.id}>
                          <TableCell
                            size="small"
                            sx={{
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              pl: `${USER_INDENT_PX}px`,
                              py: 2,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <Person
                                fontSize="small"
                                sx={{ color: colors.text.secondary }}
                              />
                              {user.name}
                            </Typography>
                          </TableCell>
                          <TableCell
                            size="small"
                            sx={{ minWidth: ACTION_COLUMN_MIN_WIDTH }}
                            align="right"
                          >
                            {/* ユーザー行にはアクションなし */}
                          </TableCell>
                        </TableRow>
                      ))}
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        }
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={e => handleMenuDelete(e, selectedRow)}
          disabled={!selectedRow}
          sx={{
            color: colors.error.main,
            '&:hover': {
              backgroundColor: colors.error.hover,
              color: colors.error.main,
            },
            gap: 1,
          }}
        >
          <DeleteOutlined />
          削除
        </MenuItem>
      </Menu>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
      >
        <DialogTitle>
          {`「${selectedRow?.name}」の削除を実行しますがよろしいですか？`}
        </DialogTitle>
        <DialogActions>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => setOpenConfirmDialog(false)}
          >
            キャンセル
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除エラーダイアログ */}
      <Dialog open={openErrorDialog} onClose={() => setOpenErrorDialog(false)}>
        <DialogTitle>
          <Typography variant="h6" component="span" fontWeight="bold">
            「{selectedRow?.name}」の削除が出来ません
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            未完了のカイゼンのメール返送先・送信先に削除対象のユーザーグループが含まれる場合、ユーザーグループの削除が出来ません。
          </Typography>
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
            削除が出来ない理由
          </Typography>
          {errorMessages.map(msg => (
            <Typography variant="body2" key={msg}>
              ・{msg}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => setOpenErrorDialog(false)}
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default UserGroupList
